import { useCallback, useMemo } from 'react';
import { ArrowLeftRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { ShipmentStatusMapping } from '../../models/shipmentStatusMapping.types';
import { MappingListRow } from './MappingListRow';

interface ShipmentStatusMappingsListProps {
  mappings: ShipmentStatusMapping[];
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onEditMapping: (mapping: ShipmentStatusMapping) => void;
  onUnmapMapping: (mapping: ShipmentStatusMapping) => void;
  canUpdate?: boolean;
}

export function ShipmentStatusMappingsList({
  mappings,
  fetchNextPage,
  hasNextPage = false,
  isFetchingNextPage = false,
  onEditMapping,
  onUnmapMapping,
  canUpdate = false,
}: ShipmentStatusMappingsListProps) {
  const { t } = useTranslation('shipmentStatusMappingModule');

  const rowData = useMemo(
    () =>
      mappings.map((mapping, index) => ({
        mapping,
        isLast: index === mappings.length - 1 && !hasNextPage,
      })),
    [mappings, hasNextPage],
  );

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      if (!hasNextPage || isFetchingNextPage || !fetchNextPage) return;

      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
      if (scrollHeight - scrollTop - clientHeight < 120) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  );

  return (
    <div className="flex max-h-full min-h-0 w-full flex-col overflow-hidden rounded-lg border border-border bg-background">
      <div className="grid shrink-0 grid-cols-[1fr_auto_2.5rem] items-center gap-3 border-b border-border bg-background px-4 py-3">
        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
          <ArrowLeftRight className="h-4 w-4 shrink-0 text-icons" />
          <span>{t('shipmentStatusMapping.list.externalStatus')}</span>
        </div>
        <span className="text-center text-xs font-medium text-muted-foreground sm:text-sm">
          {t('shipmentStatusMapping.list.internalStatus')}
        </span>
        <span className="sr-only">{t('shipmentStatusMapping.table.actions')}</span>
      </div>

      <div className="min-h-0 overflow-y-auto overscroll-contain pb-px" onScroll={handleScroll}>
        {rowData.map(({ mapping, isLast }) => (
          <MappingListRow
            key={mapping.id}
            mapping={mapping}
            isLast={isLast}
            canUpdate={canUpdate}
            onEditMapping={onEditMapping}
            onUnmapMapping={onUnmapMapping}
          />
        ))}

        {isFetchingNextPage ? (
          <p className="px-4 py-3 text-center text-xs text-muted-foreground">
            {t('shipmentStatusMapping.list.loadingMore')}
          </p>
        ) : null}
      </div>
    </div>
  );
}
